import React, { useEffect, useMemo, useRef, useState } from 'react';

const MENTION = /(?:^|\s)@([\p{L}\p{N}'.-]*(?:\s[\p{L}\p{N}'.-]*)?)$/u;
const field = 'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-meetoi-navy';

const wordsOf = (s) => (s || '').toLowerCase().split(/\s+/).filter(Boolean);

export const findMention = (text, caret) => {
  const before = text.slice(0, caret);
  const m = before.match(MENTION);
  if (!m) return null;
  return { query: m[1], start: before.length - m[1].length - 1 };
};

export const filterContacts = (contacts, query) => {
  const tokens = wordsOf(query);
  return contacts
    .filter((c) => {
      const pool = wordsOf(`${c.full_name} ${c.company || ''}`);
      return tokens.every((t) => pool.some((w) => w.startsWith(t)) || (c.email || '').toLowerCase().startsWith(t));
    })
    .slice(0, 6);
};

export const MentionInput = ({ value, onChange, contacts, onMention, onSubmit, placeholder }) => {
  const ref = useRef(null);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const mention = useMemo(() => findMention(value, caret), [value, caret]);
  const matches = useMemo(() => (mention ? filterContacts(contacts, mention.query) : []), [mention, contacts]);
  const open = Boolean(mention) && !dismissed && matches.length > 0;

  useEffect(() => { setActive(0); }, [mention?.query]);

  const choose = (c) => {
    const insert = `@${c.full_name} `;
    const next = value.slice(0, mention.start) + insert + value.slice(caret);
    const pos = mention.start + insert.length;
    onChange(next);
    onMention({ id: c.id, name: c.full_name });
    setCaret(pos);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(pos, pos);
    });
  };

  const track = (e) => setCaret(e.target.selectionStart ?? e.target.value.length);

  const onKeyDown = (e) => {
    if (open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % matches.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + matches.length) % matches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choose(matches[active]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setDismissed(true); return; }
    }
    if (e.key === 'Enter') onSubmit();
  };

  return (
    <div className="relative flex-1">
      <input
        ref={ref}
        className={field}
        value={value}
        maxLength={500}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        onChange={(e) => { setDismissed(false); onChange(e.target.value); track(e); }}
        onKeyUp={track}
        onClick={track}
        onSelect={track}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul role="listbox" className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
          {matches.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === active}>
              <button
                type="button"
                className={`block w-full px-3 py-2 text-left text-sm ${i === active ? 'bg-violet-50' : 'hover:bg-slate-50'}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(c)}
              >
                <span className="font-medium text-slate-900">{c.full_name}</span>
                <span className="block text-xs text-slate-500">{[c.company, c.email].filter(Boolean).join(' · ') || 'No company or email'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};